import type { RQLToken } from "./types";
import { RQLTokenizerError } from "./types";
import { ALLOWED_FIELD_NAMES } from "./fieldDefinitions";

const COMPARISON_OPERATORS = [">=", "<=", "!=", "=", ">", "<"] as const;
const LOGICAL_OPERATORS = ["and", "or", "AND", "OR"] as const;

export function tokenize(query: string): RQLToken[] {
  const tokens: RQLToken[] = [];
  let position = 0;
  const length = query.length;

  while (position < length) {
    const char = query[position];

    if (/\s/.test(char)) {
      position++;
      continue;
    }

    if (char === "(" || char === ")") {
      tokens.push({
        type: "parenthesis",
        value: char,
        position,
      });
      position++;
      continue;
    }

    if (char === ">" || char === "<" || char === "!" || char === "=") {
      // Check for consecutive operators
      const lastToken = tokens[tokens.length - 1];
      if (
        lastToken &&
        lastToken.type === "operator" &&
        lastToken.value !== "and" &&
        lastToken.value !== "or"
      ) {
        throw new RQLTokenizerError(
          `Unexpected operator after '${lastToken.value}'`,
          position,
        );
      }

      const operatorResult = parseOperator(query, position);
      if (!COMPARISON_OPERATORS.includes(operatorResult.value as any)) {
        throw new RQLTokenizerError(
          `Invalid operator: ${operatorResult.value}`,
          position,
        );
      }
      tokens.push({
        type: "operator",
        value: operatorResult.value,
        position,
      });
      position = operatorResult.newPosition;
      continue;
    }

    if (/[a-zA-Z_]/.test(char)) {
      const identifierResult = parseIdentifier(query, position);

      if (LOGICAL_OPERATORS.includes(identifierResult.value as any)) {
        tokens.push({
          type: "operator",
          value: identifierResult.value.toLowerCase(),
          position,
        });
      } else if (ALLOWED_FIELD_NAMES.includes(identifierResult.value)) {
        tokens.push({
          type: "field",
          value: identifierResult.value,
          position,
        });
      } else {
        throw new RQLTokenizerError(
          `Unknown field: ${identifierResult.value}. Allowed fields: ${ALLOWED_FIELD_NAMES.join(", ")}`,
          position,
        );
      }

      position = identifierResult.newPosition;
      continue;
    }

    if (/[\d-]/.test(char)) {
      const numberResult = parseIntegerNumber(query, position);
      tokens.push({
        type: "value",
        value: numberResult.value,
        position,
      });
      position = numberResult.newPosition;
      continue;
    }

    if (char === '"' || char === "'") {
      const stringResult = parseString(query, position);
      tokens.push({
        type: "value",
        value: stringResult.value,
        position,
      });
      position = stringResult.newPosition;
      continue;
    }

    throw new RQLTokenizerError(`Unexpected character: ${char}`, position);
  }

  return tokens;
}

function parseOperator(
  query: string,
  startPos: number,
): { value: string; newPosition: number } {
  const char = query[startPos];

  if (char === ">" && query[startPos + 1] === "=") {
    return { value: ">=", newPosition: startPos + 2 };
  }
  if (char === "<" && query[startPos + 1] === "=") {
    return { value: "<=", newPosition: startPos + 2 };
  }
  if (char === "!" && query[startPos + 1] === "=") {
    return { value: "!=", newPosition: startPos + 2 };
  }
  if (char === ">" || char === "<" || char === "=") {
    return { value: char, newPosition: startPos + 1 };
  }

  throw new RQLTokenizerError(
    `Invalid operator starting with: ${char}`,
    startPos,
  );
}

function parseIdentifier(
  query: string,
  startPos: number,
): { value: string; newPosition: number } {
  let position = startPos;
  let value = "";

  while (position < query.length && /[a-zA-Z0-9_]/.test(query[position])) {
    value += query[position];
    position++;
  }

  if (value.length === 0) {
    throw new RQLTokenizerError("Empty identifier", startPos);
  }

  return { value, newPosition: position };
}

function parseIntegerNumber(
  query: string,
  startPos: number,
): { value: string; newPosition: number } {
  let position = startPos;
  let value = "";

  if (query[position] === "-") {
    value += "-";
    position++;
  }

  while (position < query.length && /\d/.test(query[position])) {
    value += query[position];
    position++;
  }

  if (value === "" || value === "-") {
    throw new RQLTokenizerError("Invalid number", startPos);
  }

  return { value, newPosition: position };
}

function parseString(
  query: string,
  startPos: number,
): { value: string; newPosition: number } {
  const quote = query[startPos];
  let position = startPos + 1;
  let value = "";

  while (position < query.length && query[position] !== quote) {
    const char = query[position];

    if (char === "\\") {
      if (position + 1 >= query.length) {
        throw new RQLTokenizerError("Unterminated escape sequence", position);
      }
      const nextChar = query[position + 1];
      if (nextChar === quote || nextChar === "\\") {
        value += nextChar;
        position += 2;
      } else {
        throw new RQLTokenizerError(
          `Invalid escape sequence: \\${nextChar}`,
          position,
        );
      }
    } else {
      value += char;
      position++;
    }
  }

  if (position >= query.length) {
    throw new RQLTokenizerError("Unterminated string", startPos);
  }

  return { value, newPosition: position + 1 };
}
